import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { payoutGql, PAYOUTS_QUERY, PAYOUT_MUTATION, payoutMoney, payoutDay } from './Payouts';
import { text } from './peopleCopy';

class FailedPayouts extends React.Component {
  state = { rows: [], count: 0, page: 1, perPage: 10, search: '', loading: true,
    error: null, retrying: null, saving: false, notice: null };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);
  serverError = (res, en, ar) => this.locale() === 'ar' ? ar : ((res && res.errorMessage) || en);

  componentDidMount() { this.load(); }

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const { page, perPage, search } = this.state;
    const json = await payoutGql(PAYOUTS_QUERY, { currentPage: page, limit: perPage,
      searchList: search || undefined, payoutStatus: 'failed' }).catch(() => null);
    const res = json && json.data && json.data.getAdminPayouts;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: this.serverError(res, 'Could not load failed payouts.', 'تعذر تحميل الدفعات الفاشلة.') });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0, loading: false });
  };

  retry = async () => {
    const row = this.state.retrying;
    this.setState({ saving: true });
    const json = await payoutGql(PAYOUT_MUTATION, { id: row.id, retry: true }).catch(() => null);
    const res = json && json.data && json.data.manualPayout;
    if (!res || res.status !== 200) {
      this.setState({ saving: false, notice: { tone: 'danger',
        text: this.serverError(res, 'Transfer failed again.', 'فشل التحويل مرة أخرى. تحقق من تفاصيل الدفع ثم حاول مجدداً.') } });
      return;
    }
    this.setState({ saving: false, retrying: null, notice: { tone: 'info',
      text: this.tx(`${payoutMoney(row.amount, row.currency)} was transferred to ${row.provider}.`, `تم تحويل ${payoutMoney(row.amount, row.currency)} إلى ${row.provider}.`) } });
    this.load(true);
  };

  columns = [
    { key: 'id', label: this.tx('Payout', 'الدفعة'), render: r => <span className="mono">PO-{r.id}</span> },
    { key: 'provider', label: this.tx('Provider', 'مقدم الخدمة'), sortable: true },
    { key: 'amount', label: this.tx('Amount', 'المبلغ'), align: 'end', sortable: true,
      render: r => <span className="num">{payoutMoney(r.amount, r.currency)}</span> },
    { key: 'reason', label: this.tx('Reason', 'السبب'), render: r => <span dir="auto">{r.reason || this.tx('Stripe did not return a reason', 'لم يُرجع Stripe سبباً')}</span> },
    { key: 'date', label: this.tx('Attempted', 'وقت المحاولة'), align: 'end', sortable: true,
      render: r => <span className="mono">{payoutDay(r.date)}</span> },
    { key: 'act', label: '', align: 'end', render: r => <button className="btn sm" type="button"
      disabled={!r.eligible} title={r.eligible ? this.tx('Retry transfer', 'إعادة محاولة التحويل') : this.tx('Provider must fix their payout method first', 'يجب على مقدم الخدمة إصلاح طريقة الدفع أولاً')}
      onClick={() => this.setState({ retrying: r, notice: null })}>{this.tx('Retry', 'إعادة المحاولة')}</button> }
  ];

  render() {
    const s = this.state;
    return <AdminShell current="failed" breadcrumb={[this.tx('Money', 'المال'), this.tx('Failed payouts', 'الدفعات الفاشلة')]} title={this.tx('Failed payouts', 'الدفعات الفاشلة')}
      lead={this.tx('Transfers Stripe rejected, with the reason returned.', 'تحويلات رفضها Stripe مع عرض سبب الرفض.')}
      actions={[<a className="btn ghost" href="/siteadmin/failed-payout" key="classic">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>]}> 
      <div className="notice danger"><span className="ni">!</span><span className="nt">
        <b>{this.tx('A failed payout means a provider has not been paid.', 'الدفعة الفاشلة تعني أن مقدم الخدمة لم يُدفع له.')}</b> {this.tx('Resolve the account issue, then retry the transfer.', 'حل مشكلة الحساب ثم أعد محاولة التحويل.')}
      </span></div>
      {s.notice && <div className={`notice ${s.notice.tone}`}><span className="ni">{s.notice.tone === 'danger' ? '!' : '✓'}</span>
        <span className="nt">{s.notice.text}</span></div>}
      {s.retrying && <Modal icon="route" tone="danger" title={this.tx('Retry this transfer?', 'إعادة محاولة هذا التحويل؟')}
        sub={`${s.retrying.provider} · ${payoutMoney(s.retrying.amount, s.retrying.currency)}`}
        confirmLabel={this.tx('Retry transfer', 'إعادة محاولة التحويل')} cancelLabel={this.tx('Cancel', 'إلغاء')}
        workingLabel={this.tx('Retrying…', 'جارٍ إعادة المحاولة…')} busy={s.saving} onConfirm={this.retry}
        onClose={() => this.setState({ retrying: null })}>
        <Summary pairs={[
          [this.tx('Last failure', 'آخر فشل'), s.retrying.reason || this.tx('Stripe did not return a reason', 'لم يُرجع Stripe سبباً')],
          [this.tx('Attempted', 'وقت المحاولة'), payoutDay(s.retrying.date)]
        ]} />
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--tx-2)' }}>
          {this.tx('If the provider has not corrected their payout details, this will fail again. Confirm with them first.', 'إذا لم يصحح مقدم الخدمة تفاصيل الدفع، فسيفشل التحويل مرة أخرى. تأكد منه أولاً.')}
        </p>
      </Modal>}
      {s.error ? <div className="notice danger"><span className="ni">!</span><span className="nt">{s.error}</span></div>
        : <DataTable columns={this.columns} rows={s.rows} count={s.count} page={s.page} perPage={s.perPage}
            loading={s.loading} onPage={page => this.setState({ page }, () => this.load())}
            onPerPage={perPage => this.setState({ perPage, page: 1 }, () => this.load())}
            onSearch={search => this.setState({ search, page: 1 }, () => this.load())}
            searchPlaceholder={this.tx('Search payout ID or provider', 'ابحث بمعرّف الدفعة أو مقدم الخدمة')} emptyText={this.tx('No failed payouts', 'لا توجد دفعات فاشلة')}
            emptyHint={this.tx('Every transfer settled successfully.', 'تمت تسوية جميع التحويلات بنجاح.')}
            copy={this.locale() === 'ar' ? {
              showing: (from, to, total) => `عرض ${from}–${to} من ${total}`,
              sortedPage: 'الترتيب داخل هذه الصفحة', previousPage: 'الصفحة السابقة', nextPage: 'الصفحة التالية',
              rowsPerPage: 'عدد الصفوف في الصفحة', perPage: n => `${n} / صفحة`, rtl: true
            } : null} />}
    </AdminShell>;
  }
}

export default injectIntl(FailedPayouts);
